// Client-side mirror of server/services/usernamePolicy.js.
// Twin of app/services/usernameRules.js — keep the two in step.
//
// There is no shared package in this monorepo (client/ and app/ have separate
// bundlers and separate node_modules), so one policy necessarily has two
// mirrors. Both are input-quality conveniences only: they exist so a field can
// say "can't end with a dot" while you type instead of after a round trip, and
// so a Save button can stay disabled for input that has no chance. The server
// runs every one of these checks again and is the only authority — never treat
// a pass here as permission.

export const USERNAME_MIN = 3;
export const USERNAME_MAX = 30;
export const USERNAME_RE = /^[a-zA-Z0-9_.]{3,30}$/;

// Comparison form. Matches foldUsername on the server and the way profiles are
// looked up — so "Rooty" and "rooty" are correctly treated as the same handle
// and restyling your own capitalisation isn't reported as taken.
export const foldUsername = (name) => String(name || '').trim().toLowerCase();

export const isSameUsername = (a, b) => foldUsername(a) === foldUsername(b);

// Strip anything the field shouldn't accept as the user types. Spaces are the
// common one — a handle pasted from a chat message usually brings one along.
export const sanitiseUsernameInput = (value) =>
  String(value || '').replace(/[^a-zA-Z0-9_.]/g, '').slice(0, USERNAME_MAX);

// Returns a user-facing message, or null when the handle looks acceptable.
// Message wording is deliberately identical to the server's so the field
// doesn't appear to change its mind when the response lands.
export const validateUsernameFormat = (name) => {
  const raw = String(name || '').trim();
  if (!raw) return 'Please enter a username.';
  if (raw.length < USERNAME_MIN) return `Usernames must be at least ${USERNAME_MIN} characters.`;
  if (raw.length > USERNAME_MAX) return `Usernames can be at most ${USERNAME_MAX} characters.`;
  if (!USERNAME_RE.test(raw)) return 'Usernames can only contain letters, numbers, dots and underscores.';
  if (!/^[a-zA-Z0-9]/.test(raw)) return 'Usernames must start with a letter or number.';
  if (!/[a-zA-Z0-9]$/.test(raw)) return 'Usernames must end with a letter or number.';
  if (/[._]{2,}/.test(raw)) return 'Usernames can’t contain two dots or underscores in a row.';
  if (!/[a-zA-Z]/.test(raw)) return 'Usernames must contain at least one letter.';
  return null;
};

// The reserved list and the objectionable-word screen deliberately live only on
// the server: shipping either to the client publishes the list, and both are
// cheap enough to answer from the availability endpoint.

export const USERNAME_HINT =
  `${USERNAME_MIN}–${USERNAME_MAX} characters · letters, numbers, dots or underscores`;

// Copy only. The cooldown is enforced entirely by the server, and the client
// gates on the `usernameChangeAvailableAt` deadline it sends back — this
// constant exists so the explanatory sentence can name a number, and a drift
// here changes wording, never behaviour.
export const RENAME_COOLDOWN_DAYS = 30;
